import { useEffect } from 'react';
import { useConsent } from '../context/ConsentContext';

/**
 * Lädt Tracking-Skripte (Google Ads, GA4, Meta, LinkedIn, TikTok) dynamisch
 * – aber wirklich nur nach expliziter Einwilligung des Nutzers.
 *
 * Wird einmal in `App` gerendert und reagiert auf Änderungen der Einwilligung
 * (Consent zurückziehen → Tracker werden NICHT entfernt, das ist nach DSGVO ok,
 * solange beim nächsten Pageview keine neuen Aufrufe mehr stattfinden – wir
 * setzen `window['ga-disable-...']=true`, was Google offiziell respektiert).
 */
const injectScript = (id, src, attrs = {}) => {
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.async = true;
  s.src = src;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
};

const injectInline = (id, code) => {
  if (document.getElementById(id)) return;
  const s = document.createElement('script');
  s.id = id;
  s.innerHTML = code;
  document.head.appendChild(s);
};

const ensureGtagBootstrap = () => {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
};

const TrackingLoader = () => {
  const { config, activeTrackers } = useConsent();

  useEffect(() => {
    if (!config) return;

    // ----- Google Ads + GA4 (gtag) ---------------------------------------
    const gaId = activeTrackers.googleAnalytics ? config.googleAnalytics?.measurementId : null;
    const adsId = activeTrackers.googleAds ? config.googleAds?.conversionId : null;

    if (gaId || adsId) {
      const primaryId = gaId || adsId;
      ensureGtagBootstrap();
      injectScript(
        'smartvolt-gtag',
        `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`,
      );
      if (gaId) {
        window.gtag('config', gaId, { anonymize_ip: true, send_page_view: true });
      }
      if (adsId) {
        window.gtag('config', adsId);
      }
    } else {
      // Opt-out flags setzen, falls bereits geladen
      if (config.googleAnalytics?.measurementId) {
        window[`ga-disable-${config.googleAnalytics.measurementId}`] = true;
      }
    }

    // ----- Meta Pixel -----------------------------------------------------
    if (activeTrackers.metaPixel && config.metaPixel?.pixelId) {
      if (!window.fbq) {
        injectInline('smartvolt-meta-pixel', `
          !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
          n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
          (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
        `);
      }
      if (window.fbq) {
        window.fbq('init', config.metaPixel.pixelId);
        window.fbq('track', 'PageView');
      }
    }

    // ----- LinkedIn Insight Tag ------------------------------------------
    if (activeTrackers.linkedInInsight && config.linkedInInsight?.partnerId) {
      if (!window._linkedin_data_partner_ids) {
        window._linkedin_data_partner_ids = [];
      }
      const pid = String(config.linkedInInsight.partnerId);
      if (!window._linkedin_data_partner_ids.includes(pid)) {
        window._linkedin_data_partner_ids.push(pid);
      }
      injectInline('smartvolt-li-init', `
        (function(l) {
          if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
          window.lintrk.q=[]}
        })(window.lintrk);
      `);
      injectScript('smartvolt-linkedin', 'https://snap.licdn.com/li.lms-analytics/insight.min.js');
    }

    // ----- TikTok Pixel --------------------------------------------------
    if (activeTrackers.tiktokPixel && config.tiktokPixel?.pixelId) {
      if (!window.ttq) {
        injectInline('smartvolt-tt-init', `
          !function (w, d, t) {
            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],
            ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
            for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
            ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
            ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};
            var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;
            var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
            ttq.load("${config.tiktokPixel.pixelId}");
            ttq.page();
          }(window, document, 'ttq');
        `);
      }
    }
  }, [config, activeTrackers]);

  return null;
};

export default TrackingLoader;
